import React, { useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Icon,
  Spinner,
  Text,
  VStack,
  Flex,
  Image,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { FiAlertCircle } from "react-icons/fi";
import { bharatConnect } from "../../assets/images";
import { TransactionsHeaderRow } from "./TransactionsHeaderRow";
import { TransactionGroup } from "./TransactionGroup";
import { useAppDispatch } from "store/hooks";
import {
  selectGroupedTransactions,
  selectTransactionsMeta,
} from "store/userTransaction/userTransactionSelectors";
import { fetchTransactionsRequest } from "store/userTransaction/userTransactionSlice";

const UserTransactionsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const grouped = useSelector(selectGroupedTransactions);
  const meta = useSelector(selectTransactionsMeta);

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const canLoadMore = useMemo(() => {
    return !meta.loading && meta.hasNext;
  }, [meta.loading, meta.hasNext]);

  // initial load (if not already present from redux-persist)
  useEffect(() => {
    if (meta.initialized && grouped.length > 0) return;

    dispatch(
      fetchTransactionsRequest({
        pageNumber: 1,
        pageSize: meta.pageSize,
        mode: "replace",
      }),
    );
  }, [dispatch]); // intentionally only once

  // infinite scroll via intersection observer
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        if (!canLoadMore) return;

        dispatch(
          fetchTransactionsRequest({
            pageNumber: meta.pageNumber + 1,
            pageSize: meta.pageSize,
            mode: "append",
          }),
        );
      },
      { root: null, rootMargin: "400px", threshold: 0.01 },
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [dispatch, canLoadMore, meta.pageNumber, meta.pageSize]);

  return (
    <Box
      borderWidth="1px"
      borderColor="blackAlpha.100"
      rounded="lg"
      overflow="hidden"
      bg="white"
    >
      <Flex
        px={4}
        py={3}
        borderBottomWidth="1px"
        borderColor="gray.100"
        justify="space-between"
        align="center"
      >
        <Box>
          <Text fontSize="md" fontWeight="bold" color="gray.600">
            Transactions
          </Text>
          <Text fontSize="xs" color="gray.400">
            Showing {meta.totalCount || 0} transactions
          </Text>
        </Box>
        <Image
          src={bharatConnect}
          alt="Bharat Connect"
          h="36px" // Adjust height as needed
          objectFit="contain"
        />
      </Flex>

      <TransactionsHeaderRow />

      {meta.error && (
        <VStack
          py={8}
          px={4}
          bg="red.50"
          gap={3}
          justify="center"
          align="center"
          rounded="md"
        >
          <Icon as={FiAlertCircle} boxSize={8} color="red.500" />
          <Text
            color="red.700"
            fontSize="md"
            fontWeight="medium"
            textAlign="center"
          >
            {meta.error}
          </Text>
          <Button
            colorScheme="red"
            variant="solid"
            size="sm"
            loading={meta.loading}
            loadingText="Retrying..."
            onClick={() =>
              dispatch(
                fetchTransactionsRequest({
                  pageNumber: 1,
                  pageSize: meta.pageSize,
                  mode: "replace",
                }),
              )
            }
          >
            Retry
          </Button>
        </VStack>
      )}

      {!meta.loading && grouped.length === 0 && !meta.error && (
        <Box px={3} py={8}>
          <Text color="gray.600">No transactions found.</Text>
        </Box>
      )}

      <VStack gap={0} align="stretch">
        {grouped.map((g) => (
          <TransactionGroup key={g.title} title={g.title} items={g.items} />
        ))}
      </VStack>

      {/* Loading / Load More */}
      <Box px={3} py={4} textAlign="center">
        {meta.loading && (
          <VStack gap={2}>
            <Spinner />
            <Text fontSize="sm" color="gray.600">
              Loading transactions...
            </Text>
          </VStack>
        )}

        {!meta.loading && meta.hasNext && (
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              dispatch(
                fetchTransactionsRequest({
                  pageNumber: meta.pageNumber + 1,
                  pageSize: meta.pageSize,
                  mode: "append",
                }),
              )
            }
          >
            Load more
          </Button>
        )}

        {!meta.loading && !meta.hasNext && grouped.length > 0 && (
          <Text fontSize="sm" color="gray.500">
            End of list
          </Text>
        )}

        {/* sentinel for infinite scroll */}
        <div ref={sentinelRef} />
      </Box>
    </Box>
  );
};

export default UserTransactionsList;
